import { AfterViewInit, Component, HostListener, ViewChild, ViewContainerRef, inject } from '@angular/core';
import { Location } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';

import { Globals } from '@core/globals';
import { DropdownTeleportComponent } from '@layout/dropdown-teleport/dropdown-teleport.component';
import { PageLoaderComponent } from '@layout/page-loader/page-loader.component';
import { GlobalsService } from '@services/globals.service';
import { GtmService } from '@services/gtm.service';
import { LanguageService } from '@services/language.service';
import { ModalService } from '@services/modal.service';
import { PopoverService } from '@services/popover.service';
import { ScrolltoService } from '@services/scrollto.service';
import { SeoService } from '@services/seo.service';
import { SizesService } from '@services/sizes.service';
import { SsrService } from '@services/ssr.service';
import { TooltipService } from '@services/tooltip.service';
import { Language } from '@models/language.model';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, DropdownTeleportComponent, PageLoaderComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements AfterViewInit {
  private location = inject(Location);
  private router = inject(Router);
  private globals = inject(Globals);
  private globalsService = inject(GlobalsService);
  private gtmService = inject(GtmService);
  private languageService = inject(LanguageService);
  private modalService = inject(ModalService);
  private popoverService = inject(PopoverService);
  private scrolltoService = inject(ScrolltoService);
  private seoService = inject(SeoService);
  private sizesService = inject(SizesService);
  private ssrService = inject(SsrService);
  private tooltipService = inject(TooltipService);
  @ViewChild('modals', { read: ViewContainerRef }) modals!: ViewContainerRef;
  @ViewChild('tooltips', { read: ViewContainerRef }) tooltips!: ViewContainerRef;

  @HostListener('window:resize') onResize(): void {
    this.sizesService.setSizes();
  }

  @HostListener('click', ['$event.target']) onClick(e: HTMLElement): void {
    const modalEl: HTMLElement | null = e.closest('[dynamic-modal]');
    if (modalEl) {
      const modal = this.getAttribute(modalEl, 'app-modal');
      const modalSize = (this.getAttribute(modalEl, 'modal-size') as 'small' | 'medium' | 'large') ?? 'medium';
      const modalData = this.getAttribute(modalEl, 'modal-data');
      const modalDisabled = this.getAttribute(modalEl, 'modal-disabled') === 'true' ? true : false;
      if (modalDisabled) return;
      const data: any = modalData ? this.globalsService.jsonToString(modalData) : 'false';
      this.modalService.open('<app-' + modal + ' [data]="' + data + '"></app-' + modal + '>', modalSize);
    }
    const link: HTMLElement | null = e.closest('data-link') || e.getAttribute('data-link') ? e.closest('data-link') || e : null;
    if (link) {
      const dataLink: string | null = link.getAttribute('data-link');
      this.router.navigate([dataLink ?? '']);
    }
    const scrolltoEl: HTMLElement | null = e.closest('[dynamic-scrollto]');
    if (scrolltoEl) {
      const scrollto = this.getAttribute(scrolltoEl, 'app-scrollto');
      this.scrolltoService.scrollTo(scrollto);
    }
    const popoverEl: HTMLElement | null = e.closest('[dynamic-popover]');
    if (popoverEl) {
      const popover = this.getAttribute(popoverEl, 'app-popover');
      const popoverWidth = this.getAttribute(popoverEl, 'popover-width');
      if (popover) this.popoverService.toggle(popover, popoverWidth ? Number(popoverWidth) : undefined);
    }

    const gtm: HTMLElement | null = e.closest('[data-gtm-name]');
    if (gtm) {
      const gtmName: string | null = gtm.getAttribute('data-gtm-name');
      const gtmData: any = gtm.getAttribute('data-gtm-data') || undefined;
      const gtmPageType: boolean = gtm.getAttribute('data-gtm-page-type') ? true : false;
      const data = { event: gtmName, ...gtmData };
      if (gtmName) {
        if (gtmPageType) {
          this.gtmService.pushGtmPageType(data);
        } else {
          this.gtmService.pushGtm(data);
        }
      }
    }
  }

  getAttribute(e: HTMLElement, name: string): string | undefined {
    const data: string | null = e.getAttribute(name) || e.getAttribute('[' + name + ']');
    if (data) return data.replace(/'/g, '');
    return undefined;
  }

  constructor() {
    const language: Language = 'cs';
    this.languageService.setLanguage(language);
    this.globals.protocol = this.ssrService.isServer() ? 'http://' : 'https://';
    this.sizesService.setSizes();
    this.seoService.setCanonical(this.location.path());
    this.seoService.setRobotsIndex();
  }

  ngAfterViewInit(): void {
    this.modalService.setEntry(this.modals);
    this.tooltipService.setEntry(this.tooltips);
  }
}
